/**
 * LinkedInAdsHero — audience-aware hero section for /linkedin paid-ad landing page.
 *
 * Vendor-specific: renders a <video> element ONLY when audience === 'vendor'.
 * For SMB and general audiences the video element is absent from the DOM (AC1.4).
 *
 * Video source:
 *   EN: /videos/linkedin-vendor-en.mp4
 *   FR: /videos/linkedin-vendor-fr.mp4
 * Pre-rendered via: cd frontend && yarn remotion:render
 * Replace placeholder files with actual Remotion renders before shipping to production.
 *
 * a11y:
 *   - prefers-reduced-motion: video is paused after mount (not just hidden) so screen
 *     readers and assistive tech get a stable first frame
 *   - onError: broken video falls back to a static <img> with translatable alt text
 */

import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Zap, Shield, ArrowRight } from 'lucide-react';

/* Props: { audience, searchParams } */
const LinkedInAdsHero = ({ audience, searchParams }) => {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const [videoError, setVideoError] = useState(false);

  // Reduced-motion: pause video after mount when user prefers reduced motion
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!videoRef.current) return;

    const applyMotionPreference = () => {
      if (mq.matches && videoRef.current) {
        videoRef.current.pause();
      }
    };

    applyMotionPreference();
    mq.addEventListener('change', applyMotionPreference);
    return () => mq.removeEventListener('change', applyMotionPreference);
  }, [audience]); // re-run when audience changes (video element may remount)

  const handleHeroCTA = () => {
    if (window.posthog) {
      window.posthog.capture('linkedin_cta_clicked', {
        cta_position: 'hero',
        segment: audience,
        lang: i18n.language,
        utm_source: searchParams.get('utm_source') || undefined,
        utm_medium: searchParams.get('utm_medium') || undefined,
        utm_campaign: searchParams.get('utm_campaign') || undefined,
      });
    }
    document.querySelector('#signup-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const videoSrc =
    i18n.language === 'fr'
      ? '/videos/linkedin-vendor-fr.mp4'
      : '/videos/linkedin-vendor-en.mp4';

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8" data-testid="linkedin-hero">
      <div className={`max-w-4xl mx-auto ${audience === 'vendor' ? 'md:grid md:grid-cols-2 md:gap-10 md:items-center' : 'text-center'}`}>

        {/* Left column: text + CTA (always rendered) */}
        <div className={audience !== 'vendor' ? 'text-center' : ''}>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <div className="inline-flex items-center space-x-2 bg-secondary text-primary px-4 py-2 rounded-full text-sm font-medium">
              <Zap className="w-4 h-4" />
              <span>{t('linkedin.hero.badge')}</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 px-4 py-2 rounded-full text-sm font-medium text-foreground">
              <Shield className="w-4 h-4 text-primary" />
              <span>{t('linkedin.hero.badgeCompliance')}</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 px-4 py-2 rounded-full text-sm font-medium text-foreground">
              <span>🇨🇦</span>
              <span>{t('linkedin.hero.badgeBilingual')}</span>
            </div>
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
            {audience === 'smb' && t('linkedin.hero.titleSMB')}
            {audience === 'vendor' && t('linkedin.hero.titleVendor')}
            {audience === 'general' && t('linkedin.hero.titleGeneral')}
            <span className="block mt-2 text-primary">
              {audience === 'smb' && t('linkedin.hero.titleAccentSMB')}
              {audience === 'vendor' && t('linkedin.hero.titleAccentVendor')}
              {audience === 'general' && t('linkedin.hero.titleAccentGeneral')}
            </span>
          </h1>

          <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
            {audience === 'smb' && t('linkedin.hero.descriptionSMB')}
            {audience === 'vendor' && t('linkedin.hero.descriptionVendor')}
            {audience === 'general' && t('linkedin.hero.descriptionGeneral')}
          </p>

          <div className="flex flex-col items-center gap-4 mt-8">
            <Button
              data-testid="hero-cta-button"
              onClick={handleHeroCTA}
              className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 group shadow-lg hover:shadow-xl transition-all"
            >
              {t('linkedin.form.cta')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <p className="text-sm text-muted-foreground">
              {t('linkedin.hero.valueProp')}
            </p>
          </div>
        </div>

        {/* Right column: vendor video — DOM absent for SMB and general (AC1.4) */}
        {audience === 'vendor' && (
          <div
            className="mt-10 md:mt-0 rounded-xl overflow-hidden shadow-xl"
            data-testid="vendor-video-container"
          >
            {reducedMotion && (
              <p className="text-xs text-muted-foreground text-center mb-2">
                {t('linkedin.hero.video.reducedMotionLabel')}
              </p>
            )}

            {/* Video element — only rendered when audience === 'vendor' */}
            {!videoError ? (
              <video
                ref={videoRef}
                src={videoSrc}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={t('linkedin.hero.video.fallbackAlt')}
                data-testid="vendor-hero-video"
                onError={() => setVideoError(true)}
                className="w-full h-auto rounded-xl"
              />
            ) : (
              /* Fallback: static image when video fails to load */
              <img
                src="/logo192.png"
                alt={t('linkedin.hero.video.fallbackAlt')}
                data-testid="vendor-hero-video-fallback"
                className="w-full h-auto rounded-xl"
              />
            )}
          </div>
        )}

      </div>
    </section>
  );
};

export default LinkedInAdsHero;
